import { getDefaultOrderPercentInputId } from "testIds";
import { OrderTypes } from "@shared/types";
import CustomInputNumber from "common/Inputs/CustomInputNumber";
import React, { CSSProperties } from "react";
import { useDefaultOrder } from "@shared/features/bots/BotForm/common/hooks";
import { ErrorText, FormFieldTitle } from "features/bots/common/styles";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import BaselineSelect from "features/bots/BotForm/common/BaselineSelect";
import EqualColumnsGrid from "common/Grids/EqualColumnsGrid";
import useMeasure from "utils/hooks/useMeasure";
import Text from "common/Typography/Text";
import styled from "styled-components";
import { typography } from "@shared/common/Themes/typography";

interface DefaultOrderProps {
  orderType: OrderTypes.TAKE_PROFIT | OrderTypes.STOP;
  isCreateTrade?: boolean;
  style?: CSSProperties;
  titleStyle?: CSSProperties;
  numColumns?: number;
  hideBaseline?: boolean;
  hidePercent?: boolean;
  hidePercentTitle?: boolean;
  baselineTitle?: string;
}

const DefaultOrder = ({
  orderType,
  isCreateTrade,
  style,
  titleStyle,
  numColumns,
  hideBaseline,
  hidePercent,
  hidePercentTitle,
  baselineTitle,
}: DefaultOrderProps) => {
  const [measureRef, { width }] = useMeasure<HTMLDivElement>();
  const {
    percentInputData,
    baselineSelectorData,
    percentErrorMessage,
    percentHelperData,
    percentWarningMessage,
  } = useDefaultOrder(orderType, isCreateTrade);

  const percentInput = (
    <CustomInputNumber
      {...percentInputData}
      data-testid={getDefaultOrderPercentInputId(orderType)}
      bordered={true}
      stringMode={true}
      useDebounce={true}
    />
  );

  return (
    <>
      <EqualColumnsGrid
        numColumns={numColumns ?? width < 420 ? 1 : 2}
        ref={measureRef}
        style={style}
      >
        {!hidePercent && (
          <div>
            {!hidePercentTitle && (
              <FormFieldTitle
                as="div"
                style={{ display: "flex", alignItems: "center", ...titleStyle }}
              >
                {percentInputData.title}{" "}
                <HelperTooltip {...percentHelperData} />
              </FormFieldTitle>
            )}
            {percentInput}
          </div>
        )}
        {!hideBaseline && (
          <BaselineSelect
            titleText={baselineTitle ?? baselineSelectorData.title}
            value={baselineSelectorData.value}
            onChange={baselineSelectorData.onChange}
            placeholder={baselineSelectorData.placeholder}
            titleStyle={titleStyle}
            orderType={orderType}
          />
        )}
      </EqualColumnsGrid>
      {percentErrorMessage && !hidePercent && (
        <ErrorText>{percentErrorMessage}</ErrorText>
      )}
      {percentWarningMessage && !hidePercent && (
        <WarningText>{percentWarningMessage}</WarningText>
      )}
    </>
  );
};

export default DefaultOrder;

const WarningText = styled(Text).attrs({
  $size: typography.fontSizes.S,
  type: "warning",
})`
  margin-top: 4px;
`;
